"use client";

import { memo, useId, PropsWithChildren } from "react";
import classNames from "classnames/bind";
import { isDiorBrand } from "@JohnLewisPartnership/jl-design-system/dist/utils/brands/brands";
import { useSizeContext } from "./SizeContext";
import { SaleTagIcon } from "../SaleTagIcon/SaleTagIcon";
import styles from "./Size.module.scss";
import type { UrlObject } from "url";

const cx = classNames.bind(styles);

type SizeProps = {
  value?: string;
  unavailable?: boolean;
  type?: string;
  selected?: boolean;
  id?: string;
  className?: string;
  hasReducedPrice?: boolean;
  useNewOfferIcon?: boolean;
  reducedPriceText?: string;
  Tag?: React.JSX.ElementType;
  buttonProps?: {
    href?: UrlObject | string;
    shallow?: boolean;
    scroll?: boolean;
    replace?: boolean;
  };
};

export const Size = memo(function Size({
  children,
  value = "",
  unavailable = false,
  type = "radio",
  selected = false,
  id = "",
  className = "",
  hasReducedPrice = false,
  useNewOfferIcon = false,
  reducedPriceText = "Reduced",
  Tag = "input",
  buttonProps = {},
  ...rest
}: PropsWithChildren<SizeProps>) {
  const { name, onClick, longestLabel, brand, hasDiorPromosEnabled } =
    useSizeContext();

  const isDior = isDiorBrand(brand) && hasDiorPromosEnabled;

  const labelClasses = cx(styles.size__label, {
    size__label__unavailable: unavailable,
  });

  const ownId = `${name}_${useId()}`;

  const thresholdClass = cx({
    sizeItem__three: longestLabel === "medium",
    sizeItem__six: longestLabel === "small",
  });

  const label = hasReducedPrice ? (
    useNewOfferIcon ? (
      <span className={styles.size__reducedInline}>
        <span>{children}</span>
        <span className={styles.size__saleTagIcon} aria-hidden="true">
          <SaleTagIcon type="small" isBlackOverride={isDior} />
        </span>
      </span>
    ) : (
      <span
        className={cx(styles.size__reduced, {
          size__reduced__dior: isDior,
        })}
      >
        <span className={styles.size__text}>{children}</span>
        <span className={styles.size__reducedPriceText}>
          {reducedPriceText}
        </span>
      </span>
    )
  ) : (
    children
  );

  return (
    <li
      className={cx(
        styles.sizeItem,
        {
          [thresholdClass]: Boolean(longestLabel),
        },
        className,
      )}
      {...rest}
      data-jlds={true}
    >
      {Tag === "input" ? (
        <div data-jlds={true}>
          <input
            checked={selected}
            className={styles.size__input}
            data-testid="size:option:input"
            id={id || ownId}
            name={name}
            onChange={(e) => onClick(e.target.value)}
            type={type}
            value={value || ""}
          />
          <label className={labelClasses} htmlFor={id || ownId}>
            {label}

            {unavailable && (
              <span className={styles.size__properties}>
                {" "}
                currently unavailable
              </span>
            )}
          </label>
        </div>
      ) : (
        <Tag
          className={cx(labelClasses, {
            size__label__selected: selected,
          })}
          data-testid="size:option:button"
          onClick={() => onClick(value || "")}
          type={Tag === "button" ? "button" : undefined}
          {...buttonProps}
          data-jlds={true}
        >
          {label}
          {unavailable && (
            <span className={styles.size__properties}>
              {" "}
              currently unavailable
            </span>
          )}
        </Tag>
      )}
    </li>
  );
});
